import {
  createComponent,
  EnvironmentInjector,
  Inject,
  Injectable,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ModalAlertaComponent } from '../../modules/util/modal-alerta/modal-alerta.component';
import { BehaviorSubject, firstValueFrom, merge } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AlertaModalService {
  colaAlertas = new BehaviorSubject<number[]>([]);

  constructor(
    private injector: EnvironmentInjector,
    @Inject(DOCUMENT) private document: Document
  ) {}

  private async mostarAlerta(
    tipo: 'confirmacion' | 'exito' | 'advertencia' | 'error',
    titulo: string,
    contenido?: string,
    isContinuar?: boolean
  ) {
    const alerta = Math.floor(Math.random() * 1000000);
    const currentQueue = [...this.colaAlertas.getValue(), alerta];
    this.colaAlertas.next(currentQueue);
  
    if (currentQueue[0] !== alerta) {
      await this.esperarAlerta(alerta); // Espera su turno
    }
  
    const modalComponent = createComponent(ModalAlertaComponent, {
      environmentInjector: this.injector,
    });
  
    modalComponent.instance.tipo = tipo;
    modalComponent.instance.titulo = titulo;
    modalComponent.instance.contenido = contenido || '';
    modalComponent.instance.continuar = isContinuar != undefined ? isContinuar : false;

    const closeObservable = modalComponent.instance.closeEvent.asObservable();
    const submitObservable = modalComponent.instance.submitEvent.asObservable();
  
    modalComponent.hostView.detectChanges();
  
    // Una vez renderizado, buscamos el elemento que contiene el contenido y le aplicamos el estilo
    const contentElement = modalComponent.location.nativeElement.querySelector('.text2-tipografia-govco');
    if (contentElement) {
      contentElement.style.whiteSpace = 'pre-line';
    }
  
    const subscription = merge(closeObservable, submitObservable).subscribe(() => {
      // Remueve la alerta actual de la cola
      this.colaAlertas.next(
        this.colaAlertas.getValue().filter((id) => id !== alerta)
      );
      subscription.unsubscribe();
    });
  
    this.document.body.appendChild(modalComponent.location.nativeElement);
  
    return {
      closeObservable,
      submitObservable,
    };
  }

  async alertaAccion(
    tipo: 'confirmacion' | 'exito' | 'advertencia' | 'error',
    titulo: string,
    contenido: string,
    accionConfirmar: () => Promise<void> | void,
    accionCancelar: () => Promise<void> | void,
    isContinuar?: boolean
  ) {
    const alerta = await this.mostarAlerta(tipo, contenido, titulo, isContinuar);
    alerta.closeObservable.subscribe(async () => await accionCancelar());
    alerta.submitObservable.subscribe(async () => await accionConfirmar());
    const mergedAns = merge(alerta.closeObservable, alerta.submitObservable);
    return await firstValueFrom(mergedAns);
  }

  async confirmacion(
    titulo: string,
    contenido: string,
    accionConfirmar: () => Promise<void> | void,
    accionCancelar: () => Promise<void> | void,
    isContinuar?: boolean
  ) {
    const alerta = await this.mostarAlerta('confirmacion', contenido, titulo, isContinuar);
    alerta.closeObservable.subscribe(async () => await accionCancelar());
    alerta.submitObservable.subscribe(async () => await accionConfirmar());
    const mergedAns = merge(alerta.closeObservable, alerta.submitObservable);
    return await firstValueFrom(mergedAns);
  }

  async exito(titulo: string, contenido?: string) {
    const alerta = await this.mostarAlerta('exito', titulo, contenido);
    const mergedAns = merge(alerta.closeObservable, alerta.submitObservable);
    return firstValueFrom(mergedAns);
  }

  async advertencia(titulo: string, contenido?: string) {
    const alerta = await this.mostarAlerta('advertencia', titulo, contenido);
    const mergedAns = merge(alerta.closeObservable, alerta.submitObservable);
    return firstValueFrom(mergedAns);
  }

  async error(titulo: string, contenido?: string) {
    const alerta = await this.mostarAlerta('error', titulo, contenido);
    const mergedAns = merge(alerta.closeObservable, alerta.submitObservable);
    return firstValueFrom(mergedAns);
  }

  private async esperarAlerta(alerta: number): Promise<void> {
    return new Promise((resolve) => {
      const subscription = this.colaAlertas.subscribe((alerts) => {
        if (alerts.indexOf(alerta) === 0) {
          subscription.unsubscribe(); // Libera el recurso
          resolve(); // Resuelve la promesa
        }
      });
    });
  }
}
